import { Store } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import NotificationsIcon from "@mui/icons-material/Notifications";
import {
  Badge,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import { formatDistanceToNow } from "date-fns";
import React, { useCallback, lazy, Suspense, useEffect, useState } from "react";
import { getCompanyCode, hasSession } from "../auth/authSession";
import { SHELL_POLL_MS, startPoll } from "../utils/poll";

const ViewProduct = lazy(() => import("../Products/ViewProduct"));

// const API_BASE_URL = "${apiUrl}"; // Update this if needed

const NotificationSystem = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const [unreadNotifications, setUnreadNotifications] = useState([]);
  const [readNotifications, setReadNotifications] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [open1, setOpen1] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedCode, setSelectedCode] = useState(null);
  const [decryptedCompanyCode, setDecryptedCompanyCode] = useState("");

  useEffect(() => {
    const loader = document.getElementById("loader");
    const companyCode = getCompanyCode();
    if (!companyCode) {
      document.title = "Colombo Logistics";
    } else {
      setDecryptedCompanyCode(companyCode);
      document.title = companyCode;
    }

    if (loader) {
      loader.style.display = "none";
    }
  }, []);

  const fetchUnreadNotifications = useCallback(async () => {
    if (!hasSession()) return;

    try {
      const response = await fetch(`${apiUrl}/api/notification/unread/${decryptedCompanyCode}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
        },
      });

      if (!response.ok) throw new Error("Failed to fetch unread notifications");

      const data = await response.json();

      if (Array.isArray(data)) {
        setUnreadNotifications(data);
      }
    } catch (err) {
      console.error("Error fetching unread notifications:", err);
    }
  }, [apiUrl, decryptedCompanyCode]);

  const fetchReadNotifications = useCallback(async () => {
    if (!hasSession()) return;

    try {
      const response = await fetch(`${apiUrl}/api/notification/read/${decryptedCompanyCode}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
        },
      });

      if (!response.ok) throw new Error("Failed to fetch read notifications");

      const data = await response.json();

      if (Array.isArray(data)) {
        setReadNotifications(data);
      }
    } catch (err) {
      console.error("Error fetching read notifications:", err);
    }
  }, [apiUrl, decryptedCompanyCode]);

  const markAllAsRead = async () => {
    if (!hasSession()) return;

    // Get the unread notification IDs
    const unreadIds = unreadNotifications.map((notif) => notif.id);

    try {
      const response = await fetch(
        `${apiUrl}/api/notification/mark-as-read`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(unreadIds), // <-- Pass list of IDs here
        }
      );

      if (!response.ok) {
        throw new Error("Failed to mark notifications as read");
      }

      // Optionally refetch notifications
    } catch (err) {
      console.error("Error marking notifications as read:", err);
    }
  };

  const handleToggleDrawer = () => {
    setDrawerOpen((prev) => !prev);
    if (drawerOpen) {
      markAllAsRead();
    }
  };

  useEffect(() => {
    if (!decryptedCompanyCode || !hasSession()) return;

    return startPoll(() => {
      fetchUnreadNotifications();
      fetchReadNotifications();
    }, SHELL_POLL_MS);
  }, [decryptedCompanyCode, fetchReadNotifications, fetchUnreadNotifications]);

  const handleNotificationClick = (notification) => {
    if (notification.title === "Product Out of Stock") {
      setSelectedId(notification.productId);
      setSelectedCode(notification.productCode);
      setOpen1(true);
    }
  };

  const handleViewClose = () => {
    setOpen1(false);
    setSelectedId(null);
    setSelectedCode(null);
  };

  return (
    <>
      <IconButton color="inherit" onClick={handleToggleDrawer}>
        <Badge badgeContent={unreadNotifications.length} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Drawer anchor="right" open={drawerOpen} onClose={handleToggleDrawer}>
        <Box sx={{ width: 400, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Notifications
          </Typography>
          <List>
            {/* Unread notifications sorted by timeStamp (newest first) */}
            {unreadNotifications
              .slice()
              .sort(
                (a, b) =>
                  new Date(b.timeStamp).getTime() -
                  new Date(a.timeStamp).getTime()
              )
              .map((notification) => (
                <React.Fragment key={`unread-${notification.id}`}>
                  <ListItem
                    sx={{ backgroundColor: "#e3f2fd" }}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <ListItemText
                      primary={
                        <Typography fontWeight="bold">
                          {notification.title}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography variant="body2">
                            {notification.message}
                          </Typography>
                          {notification.title === "Product Out of Stock" && (
                            <Button
                              variant="outlined"
                              color="info"
                              sx={{
                                cursor: "pointer",
                                py: 0,
                                fontSize: "12px",
                                mt: 1,
                                mb: 1,
                              }}
                              onClick={() =>
                                handleNotificationClick(notification)
                              }
                            >
                              View Product
                            </Button>
                          )}
                          <br />
                          <Typography variant="caption" color="text.secondary">
                            {formatDistanceToNow(
                              new Date(notification.timeStamp),
                              { addSuffix: true }
                            )}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}

            {/* Read notifications sorted by timeStamp (newest first) */}
            {readNotifications
              .slice()
              .sort(
                (a, b) =>
                  new Date(b.timeStamp).getTime() -
                  new Date(a.timeStamp).getTime()
              )
              .map((notification) => (
                <React.Fragment key={`read-${notification.id}`}>
                  <ListItem sx={{ backgroundColor: "#f5f5f5" }}>
                    <ListItemText
                      primary={
                        <Typography fontWeight="medium">
                          {notification.title}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography variant="body2">
                            {notification.message}
                          </Typography>
                          {notification.title === "Product Out of Stock" && (
                            <Button
                              variant="outlined"
                              color="info"
                              sx={{
                                cursor: "pointer",
                                py: 0,
                                fontSize: "12px",
                                mt: 1,
                                mb: 1,
                              }}
                              onClick={() =>
                                handleNotificationClick(notification)
                              }
                            >
                              View Product
                            </Button>
                          )}
                          <br />
                          <Typography variant="caption" color="text.secondary">
                            {formatDistanceToNow(
                              new Date(notification.timeStamp),
                              { addSuffix: true }
                            )}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
          </List>
        </Box>
      </Drawer>
      <Dialog
        open={open1}
        onClose={() => { }}
        sx={{
          zIndex: 1300,
          "& .MuiDialog-paper": {
            maxWidth: "90%",
            width: "100%",
            minHeight: "400px",
            height: "80%",
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Store sx={{ marginRight: 1 }} /> View Product
          </Box>
          <hr />
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleViewClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "#000",
            }}
          >
            <CloseIcon sx={{ fontSize: 30, stroke: "#000", strokeWidth: 1 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {open1 ? (
            <Suspense fallback={<div>Loading...</div>}>
              <ViewProduct id={selectedId} code={selectedCode} />
            </Suspense>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default NotificationSystem;
